import { ImagePaths } from '../../lib/imageUrls.js';

export const sharepointSiteMembers = {
    id: 25,
    slug: 'sharepoint-site-members',
    ogImage: '/og/courses/sharepoint-site-members.jpg',
    courseCode: 'SHAREPOINT-MEMBER-001',
    title: 'SharePoint Site Members — Confident Contributor',
    subtitle: 'Collaborate effectively and contribute with confidence in SharePoint',
    valueProposition: 'Get productive in SharePoint fast — learn to find, create, and manage documents, collaborate with teammates, and use SharePoint features that save time and reduce email overload.',
    duration: '8 hours',
    students: 'New',
    level: 'Foundational',
    organization: 'EDUC4TE',
    image: ImagePaths.COURSE_2,
    shortDescription: 'SHAREPOINT-MEMBER-001 — SharePoint contributor skills covering documents, co-authoring, lists, and search for site members.',
    price: {
        aud: 299,
        currency: 'AUD',
        earlyBird: 249
    },
    stripeConfig: {
    priceId: 'price_1TPMQiKu1nyc322UhQZWDsop',
    productId: 'prod_UO8iYS9lU2y6iB'
  },

    snapshot: {
        whoItsFor: [
            'SharePoint Site Members and contributors',
            'Knowledge workers using Microsoft 365',
            'Teams migrating from shared drives to SharePoint',
            'Anyone replacing email attachments with SharePoint collaboration'
        ],
        accreditation: 'Aligned to Microsoft 365 adoption guidance, Microsoft Digital Skills framework, and Australian Privacy Act 1988'
    },

    logistics: {
        classSize: 'Maximum 25 participants for interactive delivery',
        requirements: [
            'Active Microsoft 365 licence with SharePoint Online access',
            'Member-level access to at least one SharePoint site',
            'Basic computer skills (file management, email, web browsing)',
            'No prior SharePoint experience required'
        ]
    },

    overview: 'SharePoint is the collaboration backbone of Microsoft 365 — but most members never learn to use it beyond opening files. This practical 8-hour course transforms SharePoint beginners into confident contributors. You will learn to navigate sites and libraries, upload and organise documents, co-author files with teammates in real time, use version history to recover earlier drafts, work with SharePoint Lists for team tracking, search effectively across SharePoint content, and integrate SharePoint with Microsoft Teams and OneDrive. The course is designed for knowledge workers who want to reduce email attachments, stop relying on shared drives, and collaborate more effectively using the tools already included in their Microsoft 365 subscription. No technical background is required — everything is taught through practical exercises using a live SharePoint environment. By the end, you will be productive in SharePoint from day one back at your desk.',

    outcomes: [
        'Navigate SharePoint sites, libraries, and lists to find content quickly and confidently',
        'Upload, organise, and manage documents in SharePoint libraries using folders and metadata',
        'Co-author documents with teammates in real time using browser-based and desktop Office applications',
        'Use version history to view, compare, and restore earlier versions of documents',
        'Create and contribute to SharePoint Lists for team tracking, approvals, and structured data',
        'Search SharePoint effectively using filters, scopes, and refiners to locate content across sites',
        'Integrate SharePoint with Microsoft Teams for seamless tab-based file collaboration',
        'Apply good SharePoint habits that reduce email overload and improve team organisation'
    ],

    audience: [
        'Knowledge workers starting to use SharePoint as part of a Microsoft 365 rollout',
        'Teams migrating from shared network drives to SharePoint Online',
        'Administrative and operations staff using SharePoint for document storage',
        'Project team members collaborating across departments in SharePoint',
        'Anyone who opens SharePoint files but wants to contribute more effectively',
        'New starters joining organisations that use SharePoint as their collaboration platform'
    ],

    prerequisites: [
        'No SharePoint experience required',
        'Basic computer skills: file management, email, and web browsing',
        'Active Microsoft 365 licence with SharePoint Online access',
        'Member or Contribute access to a SharePoint site for lab exercises'
    ],

    modules: [
        {
            title: 'Getting Started with SharePoint Online',
            duration: '1 hour',
            topics: [
                'What is SharePoint and how it fits into Microsoft 365',
                'SharePoint vs OneDrive vs Teams: understanding where things live',
                'Navigating sites: top navigation, quick launch, and search bar',
                'Understanding your role as a site member vs site owner vs visitor',
                'Accessing SharePoint from a browser, Teams tab, and the Microsoft 365 app launcher'
            ]
        },
        {
            title: 'Working with Documents and Libraries',
            duration: '2 hours',
            topics: [
                'Understanding document libraries: what they are and how they differ from folders',
                'Uploading, creating, and organising files in a library',
                'Using folders vs metadata columns for document organisation',
                'Checking in and checking out documents to prevent conflicting edits',
                'Moving and copying files within and between libraries',
                'Lab: Upload, organise, and tag documents in a shared library (25 min)'
            ]
        },
        {
            title: 'Co-authoring and Collaboration',
            duration: '2 hours',
            topics: [
                'Real-time co-authoring in Word, Excel, and PowerPoint via the browser',
                'Seeing who else is editing a document and what they are changing',
                'Comments, @mentions, and resolving feedback in Office documents',
                'Version history: viewing, comparing, and restoring previous document versions',
                'Sharing documents and links: internal sharing, anyone links, and expiry settings',
                'Lab: Co-author a document with a classmate and use version history (20 min)'
            ]
        },
        {
            title: 'SharePoint Lists and Structured Data',
            duration: '1.5 hours',
            topics: [
                'What is a SharePoint List and when to use it instead of a spreadsheet',
                'Creating and editing list items with standard and custom columns',
                'Using list views to filter, sort, and group items for different audiences',
                'Attaching files to list items and using the attachments column',
                'Following a list to receive notifications when items change',
                'Lab: Create and manage a team task tracker using a SharePoint List (20 min)'
            ]
        },
        {
            title: 'Search, Integration, and Good Habits',
            duration: '1.5 hours',
            topics: [
                'Searching SharePoint: site search vs Microsoft 365 universal search',
                'Using search refiners, filters, and file type selectors to narrow results',
                'Syncing SharePoint libraries to your desktop with OneDrive sync client',
                'Accessing SharePoint content inside Microsoft Teams via channel tabs',
                'Following sites and documents to track content you care about',
                'Good SharePoint habits: naming conventions, version hygiene, and recycle bin awareness'
            ]
        }
    ],

    format: {
        type: 'Live instructor-led workshop, self-paced online modules, or lunch-and-learn (8 x 1-hour sessions over 8 days)',
        includes: [
            '8 hours of practical instruction with a live SharePoint environment',
            '4 hands-on labs with guided step-by-step exercises',
            'SharePoint Member Quick Reference Card (PDF)',
            'Document library and list setup cheat sheet',
            'Microsoft 365 collaboration guide for everyday use',
            'Certificate of completion',
            'Access to course materials for 12 months'
        ]
    },

    nextIntake: {
        date: 'Register your interest — we will confirm the next cohort',
        details: 'Full-day instructor-led workshop (9:00 AM to 5:00 PM) or self-paced online access available immediately'
    },

    faqs: [
        {
            question: 'Do I need any prior SharePoint experience?',
            answer: 'No prior SharePoint experience is needed. This course starts from the very basics — navigating a site and opening a document — and builds practical skills progressively. If you can use email and browse the web, you have everything you need to get started.'
        },
        {
            question: 'How is SharePoint different from OneDrive?',
            answer: 'OneDrive is personal storage for your individual files, while SharePoint is shared team storage for files that multiple people need to access and collaborate on. The course explains this distinction clearly in the first module and helps you understand when to use each. Both integrate seamlessly with Microsoft Teams.'
        },
        {
            question: 'Will I need my own SharePoint site to participate in the labs?',
            answer: 'You need Member or Contribute access to a SharePoint site to complete the lab exercises. Your organisation\'s IT team can grant this, or you can use a Microsoft 365 trial tenant. We provide guidance on setting up a safe practice environment before the course begins.'
        },
        {
            question: 'Is this course suitable for teams migrating from shared network drives?',
            answer: 'Yes, this is one of the most common scenarios we see. The course specifically addresses how to think differently about organisation when moving from folder-heavy network drives to SharePoint. You will learn metadata-based organisation, library design, and how to help your team adopt new habits quickly.'
        },
        {
            question: 'Does the course cover using SharePoint inside Microsoft Teams?',
            answer: 'Yes. Module 5 covers how SharePoint libraries are connected to Teams channels and how to access SharePoint content from within Teams. Understanding this relationship reduces confusion about where files live and prevents accidental duplication across both platforms.'
        },
        {
            question: 'Can we train our whole team together at a group rate?',
            answer: 'Yes, and we strongly encourage it. When an entire team learns SharePoint together, they develop shared habits around file naming, library use, and collaboration that make the environment much more effective. For teams of 5 or more, contact us for a private quote.'
        },
        {
            question: 'What is the difference between this course and the SharePoint Site Owners course?',
            answer: 'This course is for site members — people who use and contribute to SharePoint sites. The Site Owners course is for people who configure and manage sites: setting up libraries, managing permissions, and governing content. If you are responsible for a site rather than just using it, the Site Owners course is the right fit.'
        },
        {
            question: 'Will I receive a certificate after completing the course?',
            answer: 'Yes. You will receive an Educ4te Certificate of Completion upon finishing the course. The certificate can be added to your LinkedIn profile, included in your professional development record, or shared with your manager as evidence of completed training.'
        },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ]
};
